<template>
  <div ref="rootRef" class="behaviour-core-value-select">
    <CustomSelect
      ref="customSelectRef"
      :input-id="resolvedInputId"
      :accessible-label="accessibleLabel"
      :hide-floating-label="hideFloatingLabel"
      :labelled-by="labelledBy"
      :model-value="normalizedModelValue"
      :class="classes"
      :options="visibleOptions"
      :clearable="clearable"
      :placeholder="placeholder || $t('common.select')"
      :filterable="false"
      :autoscroll="false"
      :reduce="reduce"
      :selectable="isSelectable"
      :invalid-feedback="invalidFeedback"
      :disabled="disabled"
      :append-dropdown-to-body="appendDropdownToBody"
      :append-dropdown-fixed-position="appendDropdownFixedPosition"
      :close-on-select="true"
      :data-testid="dataTestid"
      @search="onSearch"
      @update:model-value="onChange"
      @open="onOpenSelect"
      @close="onCloseSelect"
    >
      <template #list-footer>
        <div
          v-if="state.isProcessing || showLoadingLabel"
          class="p-2 text-center text-muted small"
        >
          {{ $t("common.loading_more") }}
        </div>
      </template>
      <template #no-options>
        <span class="text-black-50 pm-1 d-block">
          {{ $t("common.sorry_no_matching_options") }}
        </span>
      </template>
    </CustomSelect>
    <output
      v-if="state.isProcessing"
      class="visually-hidden"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ $t("common.loading_more") }}
    </output>
  </div>
</template>

<script>
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import {
  dedupeLazySelectOptions,
  getLazySelectDropdownMenu,
  restoreLazySelectDropdownScroll,
  scheduleLazySelectLoadMore
} from "@/helpers/behaviourLazySelectScroll.js"
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch
} from "vue"
import { useStore } from "vuex"

const SEARCH_DEBOUNCE_MS = 320
const PER_PAGE = 25

export default {
  name: "BehaviourCoreValueLazySelect",
  components: { CustomSelect },
  props: {
    /** Selected value (id) or "all" / null. */
    modelValue: {
      type: [String, Number, Object],
      default: null
    },
    /** Pre-selected `{ label, value }` displayed before the page is fetched. */
    selectedOption: {
      type: Object,
      default: null
    },
    /** Items prepended to the option list (e.g. `{ label: "All", value: "all" }`). */
    prependItems: {
      type: Array,
      default: () => []
    },
    /** Ids hidden from the option list (e.g. the core value being deleted). */
    excludedIds: {
      type: Array,
      default: () => []
    },
    /** Server-side `status` filter: `"all" | "active" | "inactive"`. */
    statusFilter: {
      type: String,
      default: "all"
    },
    /** vue-select `reduce`. Default returns `option.value` so v-model is the id. */
    reduce: {
      type: Function,
      default: (option) =>
        option && option.value != null ? option.value : null
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    inputId: { type: String, default: "" },
    accessibleLabel: { type: String, default: "" },
    hideFloatingLabel: { type: Boolean, default: false },
    labelledBy: { type: String, default: "" },
    invalidFeedback: { type: [String, Array], default: "" },
    classes: { type: [String, Object, Array], default: "" },
    appendDropdownToBody: { type: Boolean, default: false },
    appendDropdownFixedPosition: { type: Boolean, default: false },
    dataTestid: { type: String, default: "" },
    /**
     * Prefetch the first page on mount so saved labels resolve even when the
     * API didn't include the relation.
     */
    prefetchOnMount: { type: Boolean, default: true }
  },
  emits: ["update:modelValue", "changed", "option-selected"],
  setup(props, { emit }) {
    const store = useStore()
    const rootRef = ref(null)
    const customSelectRef = ref(null)
    const instance = getCurrentInstance()

    let detachScroll = null
    let searchTimeoutId = null
    let loadMoreTimeoutId = null

    const state = reactive({
      options: [],
      page: 1,
      lastPage: null,
      searchQuery: "",
      isProcessing: false,
      hasFetched: false,
      isOpen: false
    })

    const resolvedInputId = computed(
      () => props.inputId || `behaviour-cv-lazy-${instance?.uid ?? ""}`
    )

    /**
     * Normalize ids to strings so vue-select's `JSON.stringify` comparison
     * (used in `findOptionFromReducedValue`) matches across `Number`/`String`.
     */
    const normalizedModelValue = computed(() => {
      const v = props.modelValue
      if (v == null || v === "") return v
      if (typeof v === "object") return v
      return String(v)
    })

    const isLastPage = computed(
      () => state.lastPage != null && state.lastPage <= state.page
    )

    const showLoadingLabel = computed(
      () => state.hasFetched && !isLastPage.value && !state.isProcessing
    )

    const visibleOptions = computed(() => {
      const prepend = (props.prependItems || [])
        .filter((item) => {
          if (!state.searchQuery) return true
          return String(item?.label || "")
            .toLowerCase()
            .includes(state.searchQuery.toLowerCase())
        })
        .map((item) => ({
          label: String(item?.label ?? ""),
          value:
            item?.value == null || typeof item?.value === "object"
              ? item?.value
              : String(item.value)
        }))
      const excluded = (props.excludedIds || [])
        .map((id) => String(id))
        .filter(Boolean)
      let opts = (state.options || []).filter(
        (opt) => !excluded.includes(String(opt?.value))
      )

      const sel = props.selectedOption
      if (
        sel &&
        sel.value != null &&
        sel.value !== "" &&
        !excluded.includes(String(sel.value)) &&
        !opts.some((opt) => String(opt?.value) === String(sel.value)) &&
        !prepend.some((opt) => String(opt?.value) === String(sel.value))
      ) {
        const rawLabel = sel.label
        const labelStr =
          rawLabel != null && String(rawLabel).trim() !== ""
            ? String(rawLabel)
            : String(sel.value)
        opts = [{ label: labelStr, value: String(sel.value) }, ...opts]
      }

      return [...prepend, ...opts]
    })

    const isSelectable = (option) => {
      const excluded = (props.excludedIds || []).map((id) => String(id))
      return !excluded.includes(String(option?.value))
    }

    const fetchPage = async ({ append = false } = {}) => {
      try {
        state.isProcessing = true
        const { rows, pagination } = await store.dispatch(
          "behaviourSettings/fetchCoreValuesList",
          {
            page: state.page,
            perPage: PER_PAGE,
            search: state.searchQuery,
            statusFilter: props.statusFilter || "all"
          }
        )
        const newOptions = (rows || [])
          .filter((row) => row?.id != null)
          .map((row) => ({
            label: String(row?.name ?? row?.id ?? ""),
            value: String(row.id)
          }))
        if (append) {
          const menu = getLazySelectDropdownMenu(customSelectRef)
          const scrollTop = menu?.scrollTop ?? 0
          const uniqueOptions = dedupeLazySelectOptions(
            state.options,
            newOptions
          )
          state.options.push(...uniqueOptions)
          await restoreLazySelectDropdownScroll(customSelectRef, scrollTop)
        } else {
          state.options = newOptions
        }
        state.lastPage = Number(pagination?.total_pages) || 1
        state.hasFetched = true
      } catch {
        if (!append) state.options = []
      } finally {
        state.isProcessing = false
      }
    }

    const onSearch = (query) => {
      const q = query == null ? "" : String(query)
      // Mirror LazyLoadSelect: skip 1-char queries to reduce server load.
      if (q.replace(/\s+/g, "").length === 1) return
      if (searchTimeoutId) clearTimeout(searchTimeoutId)
      searchTimeoutId = setTimeout(() => {
        state.searchQuery = q
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }, SEARCH_DEBOUNCE_MS)
    }

    const onChange = (value) => {
      emit("update:modelValue", value)
      emit("changed", value)
      const matched = (visibleOptions.value || []).find(
        (opt) => opt && String(opt.value) === String(value)
      )
      if (matched) emit("option-selected", { ...matched })
    }

    const handleScroll = (event) => {
      const menu = event.target
      if (!menu || typeof menu.scrollTop !== "number") return
      if (!menu.classList?.contains?.("vs__dropdown-menu")) return
      if (!state.options.length || isLastPage.value || state.isProcessing) {
        return
      }
      if (menu.offsetHeight + menu.scrollTop + 8 >= menu.scrollHeight) {
        loadMore()
      }
    }

    const loadMore = () => {
      scheduleLazySelectLoadMore({
        isBlocked: () => state.isProcessing || isLastPage.value,
        onLoad: () => {
          state.page += 1
          fetchPage({ append: true })
        },
        getTimeoutId: () => loadMoreTimeoutId,
        setTimeoutId: (id) => {
          loadMoreTimeoutId = id
        }
      })
    }

    const removeScrollListener = () => {
      if (typeof detachScroll === "function") detachScroll()
      detachScroll = null
    }

    const attachScrollListener = () => {
      removeScrollListener()
      if (props.appendDropdownToBody) {
        nextTick(() => {
          requestAnimationFrame(() => {
            const menu =
              customSelectRef.value?.getDropdownMenuElement?.() || null
            if (menu?.addEventListener) {
              menu.addEventListener("scroll", handleScroll, { passive: true })
              detachScroll = () => {
                menu.removeEventListener("scroll", handleScroll)
              }
            }
          })
        })
      } else {
        const root = rootRef.value
        if (root?.addEventListener) {
          root.addEventListener("scroll", handleScroll, {
            capture: true,
            passive: true
          })
          detachScroll = () => {
            root.removeEventListener("scroll", handleScroll, { capture: true })
          }
        }
      }
    }

    const onOpenSelect = () => {
      state.isOpen = true
      if (!state.hasFetched && !props.disabled) {
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }
      attachScrollListener()
    }

    const onCloseSelect = () => {
      state.isOpen = false
      removeScrollListener()
    }

    watch(
      () => props.statusFilter,
      () => {
        if (state.hasFetched) {
          state.page = 1
          state.lastPage = null
          fetchPage({ append: false })
        }
      }
    )

    onMounted(() => {
      if (props.prefetchOnMount && !state.hasFetched) {
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }
    })

    onUnmounted(() => {
      removeScrollListener()
      if (searchTimeoutId) clearTimeout(searchTimeoutId)
      if (loadMoreTimeoutId) clearTimeout(loadMoreTimeoutId)
    })

    return {
      rootRef,
      customSelectRef,
      state,
      resolvedInputId,
      normalizedModelValue,
      visibleOptions,
      showLoadingLabel,
      isSelectable,
      onSearch,
      onChange,
      onOpenSelect,
      onCloseSelect
    }
  }
}
</script>
